import { useSearchParams } from 'next/navigation'
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'

import { captureUtms } from '@/lib/attribution/landingUtms'
import { getLeadId, getStoredLeadId, setStoredLeadId } from '@/lib/attribution/leadId'

/**
 * Holds the attribution `lead_id` IN MEMORY for the session (WEB-903).
 *
 * On landing the id arrives on the URL; this provider captures it and keeps it
 * for the session. NOTHING is ever written to the device — no cookie,
 * localStorage or sessionStorage — so the PECR consent trigger is never met
 * and attribution behaves identically for every consent state. (Deliberately
 * separate from the localStorage-backed LeadProvider.)
 *
 * Reload survival needs no device storage either: `TrackedLink` /
 * `useTrackedRouter` re-append the id to internal navigations, so the URL
 * keeps carrying it and a hard reload recaptures it here on mount. The Stripe
 * `return_url` covers the checkout hard-nav boundary the same way.
 */
interface AttributionContextProps {
  leadId: string | null
}

const AttributionContext = createContext<AttributionContextProps>({ leadId: null })

export const AttributionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const searchParams = useSearchParams()
  const [leadId, setLeadId] = useState<string | null>(getStoredLeadId)

  // Capture from the URL on landing (and on any navigation that carries it).
  useEffect(() => {
    const fromUrl = getLeadId(searchParams)
    if (fromUrl) {
      setStoredLeadId(fromUrl)
      setLeadId(fromUrl)
    }
  }, [searchParams])

  // Capture the landing UTMs in memory (PECR-clean, like the lead_id above);
  // the Braze provider reads them once marketing consent initialises the SDK.
  useEffect(() => {
    captureUtms(searchParams ?? undefined)
  }, [searchParams])

  const value = useMemo(() => ({ leadId }), [leadId])

  return <AttributionContext.Provider value={value}>{children}</AttributionContext.Provider>
}

export const useLeadId = (): string | null => useContext(AttributionContext).leadId
